import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import KPITiles from "@/components/KPITiles";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "@/context/AuthContext";
import { Map as MapIcon, PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATUS_HEX, STATUS_LABEL, personInitials, effectiveStatus, markerColor, UNASSIGNED_NEXT_DAY_HEX } from "@/lib/orderStatus";
import { useRegion } from "@/context/RegionContext";
import { regionBoundsCorners } from "@/lib/mapBounds";

const STATUS_COLOR = STATUS_HEX;

// Always frame the pharmacy staff overview map on the active workspace
// bounds (Miami-Dade + Broward for South FL, Orlando corridor for Central
// FL). Stray out-of-region orders still render as markers but do NOT
// hijack the framing — the operator wants the map centered on their
// actual service area, not on a distant test address.
function FitToWorkspace({ region }) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLngBounds(regionBoundsCorners(region));
    map.fitBounds(bounds, { padding: [16, 16] });
  }, [region, map]);
  return null;
}

const pharmacyInitials = (name) => personInitials(name, "PH");

const pharmacyIcon = (name) => L.divIcon({
  className: "",
  html: `<div style="background:#111;color:#fff;width:31px;height:31px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;letter-spacing:.5px;border:3px solid #fff;box-shadow:0 4px 8px rgba(0,0,0,.25);">${pharmacyInitials(name)}</div>`,
  iconSize: [31, 31], iconAnchor: [15, 15],
});

const PICKUP_COLOR = "#7C3AED"; // purple — distinct color for Pick Up orders

// Popup accent labels (same palette used on the Super Admin overview map).
const DELIVER_TYPE_LABEL = {
  same_day: "Same Day",
  next_day: "Next Day",
  otc: "OTC",
  pickup: "Pick Up",
};
const PRIORITY_LABEL = {
  normal: "Normal",
  urgent: "Urgent",
  cold_chain: "Cold Chain",
  clinic: "Clinic",
  business: "Business",
};
const PRIORITY_COLOR = {
  normal: "#64748b",
  urgent: "#dc2626",
  cold_chain: "#0ea5e9",
  clinic: "#8b5cf6",
  business: "#F59E0B",
};

const orderIcon = (status, name, isPickup = false, order = null) => {
  if (isPickup) {
    return L.divIcon({
      className: "",
      html: `<div style="position:relative;background:${PICKUP_COLOR};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:9px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 6px rgba(124,58,237,.5);">${personInitials(name)}<span style="position:absolute;top:-6px;left:-6px;background:#4C1D95;color:#fff;font-size:8px;font-weight:900;width:15px;height:15px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;letter-spacing:0;">P</span></div>`,
      iconSize: [26, 26], iconAnchor: [13, 13],
    });
  }
  // Unassigned + Next-Day picks up the darker amber shade so pharmacy staff
  // can visually separate tomorrow's queue from today's on their overview.
  const color = markerColor(order || { deliver_type: null }, status) || STATUS_COLOR[status] || "#666";
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:9px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.25);">${personInitials(name)}</div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
};

// Driver marker: small pulsing dot showing live drivers on the map.
// Blue when on a route, green when online (idle), grey if offline (filtered out anyway).
const DRIVER_STATUS_COLOR = { on_route: "#3a86ff", online: "#16A34A", offline: "#9ca3af" };
const driverIcon = (status = "on_route") => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:30px;height:30px;"><div style="position:absolute;inset:0;background:${DRIVER_STATUS_COLOR[status]};border-radius:50%;opacity:.25;animation:pulse 1.6s ease-out infinite;"></div><div style="position:absolute;inset:4px;background:${DRIVER_STATUS_COLOR[status]};border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);">🚐</div></div><style>@keyframes pulse{0%{transform:scale(.6);opacity:.5;}100%{transform:scale(1.6);opacity:0;}}</style>`,
  iconSize: [30, 30], iconAnchor: [15, 15],
});

const MIAMI_BROWARD_CENTER = [26.0, -80.25];

export default function PharmacyStaffOverview() {
  const { user } = useAuth();
  const { active: activeRegion } = useRegion();
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [pharmacy, setPharmacy] = useState(null);
  const [liveDrivers, setLiveDrivers] = useState([]);
  const [scope, setScope] = useState("week"); // "month" | "week"

  useEffect(() => {
    const load = () => Promise.all([
      api.get("/stats/overview", { params: { scope } }),
      api.get("/orders"),
      api.get("/pharmacies").catch(() => ({ data: [] })),
      api.get("/drivers/live").catch(() => ({ data: [] })),
    ]).then(([s, o, p, ld]) => {
      setStats(s.data);
      setOrders(o.data);
      setPharmacy((p.data || []).find((x) => x.id === user?.pharmacy_id) || null);
      setLiveDrivers(ld.data || []);
    });
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [user?.pharmacy_id, scope]);

  // Show every geocoded order on the map — pin color follows the current status so
  // delivered/failed orders also remain visible (in green / red respectively).
  // EXCEPT: for Pharmacy Staff, hide DELIVERED orders whose `delivered_at`
  // is older than 24 hours so the map stays focused on active work.
  const geocoded = useMemo(() => {
    const cutoffMs = Date.now() - 24 * 60 * 60 * 1000;
    return orders.filter((o) => {
      if (o.lat == null || o.lng == null) return false;
      if (o.status === "delivered" && o.delivered_at) {
        const t = Date.parse(o.delivered_at);
        if (!Number.isNaN(t) && t < cutoffMs) return false;
      }
      return true;
    });
  }, [orders]);

  // Online drivers with a known location.
  const driversOnMap = useMemo(
    () => (liveDrivers || []).filter((d) => d.status !== "offline" && d.lat != null && d.lng != null),
    [liveDrivers]
  );

  const center = geocoded[0] ? [geocoded[0].lat, geocoded[0].lng] : activeRegion.center;

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="pharmacy-staff-overview-page">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Overview</h1>
          <p className="text-muted-foreground mt-1.5">Live map of your pharmacy's active orders.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatsScopeToggle value={scope} onChange={setScope} />
          <Link to="/staff/new">
            <Button className="rounded-full" data-testid="overview-new-order-btn">
              <PackagePlus className="h-4 w-4 mr-1.5" /> New order
            </Button>
          </Link>
        </div>
      </header>

      <KPITiles
        items={[
          { label: "Unassigned", value: stats.out_of_route, hint: "Out of route", color: "yellow", to: "/staff/orders?status=unassigned" },
          {
            label: "Accepted",
            value: stats.received_from_pharmacy,
            hint: stats.pending_intake ? `${stats.pending_intake} pending intake` : "from pharmacy",
            color: "lightBlue",
            to: "/staff/orders?status=accepted",
          },
          { label: "On Route", value: stats.routed, hint: "Orders on a route", color: "sky", to: "/staff/orders?status=on_route" },
          { label: "Delivered", value: stats.delivered, hint: "Completed today", color: "green", to: "/staff/orders?status=delivered" },
          { label: "Failed", value: stats.failed, hint: "Could not deliver", color: "red", to: "/staff/orders?status=failed" },
          { label: "Returned", value: stats.returned, hint: "Returned to pharmacy", color: "orange", to: "/staff/orders?status=returned" },
          { label: "Total Open", value: stats.open_orders, hint: "All open (not delivered)", to: "/staff/orders" },
          {
            label: "Copays",
            value: stats.copay_collected_count ?? 0,
            hint: stats.copay_outstanding_count
              ? `${stats.copay_outstanding_count} outstanding · $${(stats.copay_outstanding_total ?? 0).toFixed(2)}`
              : `$${(stats.copay_collected_total ?? 0).toFixed(2)} collected`,
            color: "white",
            to: "/staff/orders?copay=collected",
          },
        ]}
      />

      <Card className="mt-6 p-2 rounded-xl border-border bg-card/80 mb-6" data-testid="staff-overview-map">
        <div className="rounded-lg overflow-hidden" style={{ height: 460 }}>
          <MapContainer center={center} zoom={activeRegion.zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <FitToWorkspace region={activeRegion.id} />
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            {/* Depot/pharmacy markers intentionally hidden — depots are only shown in the Route Builder. */}
            {geocoded.map((o) => {
              const st = effectiveStatus(o);
              const isPickup = (o.deliver_type || "").toLowerCase() === "pickup";
              return (
                <Marker key={`${o.id}:${st}:${isPickup ? "p" : "d"}`} position={[o.lat, o.lng]} icon={orderIcon(st, o.customer_name, isPickup, o)}>
                  <Popup>
                    {/* Single header line: Pharmacy Alias – Deliver Type – Priority.
                        Pharmacy alias is typically their own on this page, so
                        it may be omitted; the filter keeps the line clean. */}
                    <div className="text-[11px] font-semibold text-black mb-1" data-testid={`staff-overview-header-${o.id}`}>
                      {[
                        o.pharmacy_nickname || o.pharmacy_name,
                        o.deliver_type ? (DELIVER_TYPE_LABEL[o.deliver_type] || o.deliver_type) : null,
                        o.priority && o.priority !== "normal" ? (PRIORITY_LABEL[o.priority] || o.priority) : null,
                      ].filter(Boolean).join(" – ")}
                    </div>
                    <div className="font-medium">{o.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{o.address}</div>
                    {isPickup && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#ede4ff", color: "#5B21B6" }}>
                        Pick Up
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: isPickup ? PICKUP_COLOR : markerColor(o, st) }}>{isPickup ? "PICK UP" : (STATUS_LABEL[st] || o.status)}</div>
                  </Popup>
                </Marker>
              );
            })}
            {/* Online drivers — pulsing van markers */}
            {driversOnMap.map((d) => (
              <Marker key={`driver-${d.id}:${d.status}`} position={[d.lat, d.lng]} icon={driverIcon(d.status)}>
                <Popup>
                  <div className="font-medium">{d.name}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DRIVER_STATUS_COLOR[d.status] }}>
                    {d.status === "on_route" ? "ON ROUTE" : "ONLINE"}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLOR.pending }} />Unassigned</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLOR.assigned }} />On Route</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLOR.delivered }} />Delivered</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#3a86ff" }} />🚐 Driver on route</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#16A34A" }} />🚐 Driver online</span>
          <span className="inline-flex items-center gap-1.5" data-testid="legend-pickup"><span className="h-2.5 w-2.5 rounded-full border-2" style={{ background: "#f3e8ff", borderColor: PICKUP_COLOR }} />Pick Up order</span>
          <span className="ml-auto text-muted-foreground"><span className="font-mono-num font-semibold text-foreground">{geocoded.length}</span> orders · <span className="font-mono-num font-semibold text-foreground">{driversOnMap.length}</span> drivers</span>
        </div>
      </Card>

      <div className="mt-6 text-xs text-muted-foreground flex items-center gap-2">
        <MapIcon className="h-3.5 w-3.5" />
        Delivered and failed orders are hidden from the map. Switch to "My Orders" for the full history.
      </div>
    </div>
  );
}
